import { authorizedServerFetch } from '@/actions/server.actions';
import { orgConfig, orgIds } from '@/constants/orgConfig.constant';
import { getAllInfoByISO } from 'iso-country-currency';
import {
  filter,
  find,
  isArray,
  isEmpty,
  isNaN,
  isNumber,
  isObject,
  isString,
  size,
  take,
  toLower,
  trim,
} from 'lodash-es';
import { defaults } from 'lodash-es';

export function abbreviateNumber(num) {
  const castedNum = Number(num);
  const numVal = isNumber(castedNum) && !isNaN(castedNum) ? castedNum : 0;
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 1,
    notation: 'compact',
    compactDisplay: 'short',
  }).format(numVal);
}

export function formatCurrency(num, currencyCode) {
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 2,
    style: 'currency',
    currency: currencyCode,
  }).format(num);
}

const getLocaleCode = () => 'en-US';

export function formatNumberForLocale(number, options = {}) {
  if (number === null || number === undefined) {
    return '0';
  }

  const formatOptions = defaults(options, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    roundingMode: 'ceil',
  });
  const formatter = new Intl.NumberFormat(getLocaleCode(), formatOptions);
  return formatter.format(number);
}

export const getAvatarText = (name, maxLetters = 2) => {
  if (isString(name)) {
    const words = name.split(' ').filter((word) => size(trim(word)) > 0);
    return take(words, maxLetters)
      .map((word) => trim(word[0]).toUpperCase())
      .join('');
  }
  return '';
};

export const getNumFollowersText = (numFollowers, abbreviate = true) => {
  if (numFollowers === 1) {
    return '1 follower';
  }
  return `${
    abbreviate ? abbreviateNumber(numFollowers) : numFollowers
  } followers`;
};

export const getNumDonorsText = (numDonors, abbreviate = true) => {
  if (numDonors === 1) {
    return '1 donor';
  }
  return `${abbreviate ? abbreviateNumber(numDonors) : numDonors} donors`;
};

export const getFullName = ({ first_name, last_name }) =>
  `${first_name} ${last_name}`;

export const getQueryStringParams = (query) =>
  query
    ? (/^[?#]/.test(query) ? query.slice(1) : query)
        .split('&')
        .reduce((params, param) => {
          const [key, value] = param.split('=');
          // eslint-disable-next-line no-param-reassign
          params[key] = value
            ? decodeURIComponent(value.replace(/\+/g, ' '))
            : '';
          return params;
        }, {})
    : {};

export const getCountryName = (countryCode) => {
  try {
    return getAllInfoByISO(countryCode).countryName;
  } catch (e) {
    return countryCode;
  }
};

export const maskText = (text, numTrailingCharactersToShow = 0) => {
  if (isString(text) && text.length > numTrailingCharactersToShow) {
    const trailingText = text.substring(
      text.length - numTrailingCharactersToShow,
    );
    const mask = '*'.repeat(text.length - numTrailingCharactersToShow);
    return mask + trailingText;
  }
  return text;
};

export const formatWebsiteUrl = (url) => {
  if (isString(url)) {
    const trimmed = trim(url);
    if (
      trimmed &&
      !trimmed.startsWith('http://') &&
      !trimmed.startsWith('https://')
    ) {
      return `https://${trimmed}`;
    }
    return trimmed;
  }
  return '';
};

export const joinWithCommas = (arr) => {
  if (arr.length === 0) {
    return '';
  }
  if (arr.length === 1) {
    return arr[0];
  }
  if (arr.length === 2) {
    return arr.join(' and ');
  }
  return `${arr.slice(0, -1).join(', ')} and ${arr.slice(-1)}`;
};

export const parseAddress = (address) => {
  if (isArray(address)) {
    return parseAddress(address[0]);
  }

  let parsedAddress = address;
  try {
    parsedAddress = isString(address) ? JSON.parse(address) : address;
  } catch {}

  const streetDetails = isObject(parsedAddress)
    ? filter(
        parsedAddress,
        (v, k) =>
          (toLower(k).startsWith('address') || toLower(k).startsWith('line')) &&
          !isEmpty(v),
      )
    : [parsedAddress];

  const formatted = streetDetails.map((streetDetail) => {
    if (isString(streetDetail)) {
      return streetDetail.replace(/^"(.*)"$/, function (match, p1) {
        return p1; // Returns the captured string without the wrapping quotes
      });
    } else {
      return '';
    }
  });

  return {
    address: formatted.join(', '),
    city: parsedAddress?.city || '',
    state: parsedAddress?.state || '',
    country: getCountryName(parsedAddress?.country || ''),
    zipcode:
      parsedAddress?.zipcode ||
      find(
        address,
        (val, key) =>
          toLower(key).startsWith('zip') ||
          toLower(key).startsWith('pin') ||
          toLower(key).startsWith('postal'),
      )?.toString() ||
      '',
  };
};

export const joinUrl = (...args) => {
  const parts = args.map((arg, index) => {
    if (arg.endsWith('/')) {
      return arg.slice(0, -1);
    }
    if (index > 0 && arg.startsWith('/')) {
      return arg.slice(1);
    }
    return arg;
  });
  return parts.join('/');
};

// Helper function to check if a URL is a video link
export const isVideoUrl = (url) => {
  const videoExtensions = [
    '.mp4',
    '.avi',
    '.mkv',
    '.mov',
    '.wmv',
    '.flv',
    '.webm',
    '.mpeg',
    '.mpg',
    '.3gp',
    '.ogv',
    '.rm',
    '.rmvb',
    '.m4v',
    '.divx',
    '.h264',
    '.h265',
  ];

  // Extract the file extension from the URL
  const urlLower = url.toLowerCase();
  const fileExtension = urlLower.split('.').pop();

  // Check if the file extension is in the list of known video extensions
  return videoExtensions.includes('.' + fileExtension);
};

export const getEnvBaseUrl = (type) => {
  if (type === 'admin') {
    return process.env.NEXT_PUBLIC_ADMIN_APP_URL;
  }

  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }

  return process.env.NEXT_PUBLIC_APP_URL;
};

export const getClientIronSession = async () => {
  const res = await fetch('/api/session', {
    credentials: 'include',
  });

  const data = await res.json();
  return data;
};

export const getAppUrlByOrgId = (orgId) => {
  if (orgId && orgIds[orgId]) {
    const configId = orgIds[orgId];
    return orgConfig[configId].clientUrl;
  }

  return getEnvBaseUrl();
};

export const getAdminLoginUrl = (email, token) => {
  const nekiAdminBaseUrl = getEnvBaseUrl('admin');
  if (token && email) {
    return `${nekiAdminBaseUrl}/almashines/login?token=${token}&email=${email}`;
  }

  return nekiAdminBaseUrl;
};

export const getLoginUrlByOrgId = (orgId) => {
  if (orgId && orgIds[orgId]) {
    const configId = orgIds[orgId];
    return orgConfig[configId].loginUrl;
  }

  return null;
};

export const validateEmail = async (email) => {
  try {
    // Make an API call to check if the email is already registered
    // const res = await authorizedServerFetch(`common/auth/verify/${email}`);
    const res = await fetch(
      `${process.env.NEXT_PUBLIC_API_HOST}/v1/public/auth/verify/${email}`,
    );
    const data = await res.json();
    console.log('validateEmail', email, data);
    return !!data?.data?.exists;
  } catch (error) {
    // Handle API call error
    console.error('Error checking email:', error);
    return 'Failed to validate email';
  }
};

export const truncateTextWithCompleteWords = (text, maxLength) => {
  if (!text) {
    return '';
  }

  if (text.length <= maxLength) return text;

  const truncated = text.slice(0, maxLength);
  const lastSpaceIndex = truncated.lastIndexOf(' ');

  return lastSpaceIndex > 0 ? truncated.slice(0, lastSpaceIndex) : truncated;
};
